import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, Subject } from 'rxjs';
import { AuthService } from '../../../core/auth/auth.service';
import { ApiError } from '../../../core/transport/api-error';
import { catalogReturnTarget } from '../../../core/auth/catalog-return-target';

@Component({
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LoginComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  showPassword: boolean;

  loginForm: FormGroup;

  errorMessage: string | null = null;

  loading$: Subject<boolean | null> = new Subject();

  constructor() {
    const formBuilder = this.formBuilder;

    this.showPassword = false;
    this.loginForm = formBuilder.group({
      email: [null, [Validators.required, Validators.email]],
      password: [null, [Validators.required]]
    });
  }

  showError(controlName: string, errorCode: string): boolean {
    const control = this.loginForm.get(controlName);

    return !!control && control.touched && control.hasError(errorCode);
  }

  onSubmit(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.errorMessage = null;
    this.loading$.next(true);

    this.authService.login(this.loginForm.value).pipe(
      finalize(() => {
        this.loading$.next(false);
      })
    ).subscribe({
      next: () => {
        const target = catalogReturnTarget(this.route.snapshot.queryParamMap.get('catalog_return'));
        if (target) void this.router.navigateByUrl(target);
        else void this.router.navigate(['/']);
      },
      error: (error: ApiError) => {
        this.errorMessage = error?.status >= 500
          ? 'Sign in failed. Please try again.'
          : (error?.message || 'Sign in failed. Please try again.');
        this.changeDetectorRef.markForCheck();
      },
    });
  }

}
